import React, { useState, useEffect } from 'react';
import { Table, Icon, Button, Loader, Message, Dimmer } from 'semantic-ui-react';
import { DateToStringDate } from '../../../../../services/date';
import { maskMoney } from '../../../../../services/masks';
import {
  getBondInsurancePoliciesList,
  getBondInsurancePolicyStatusLabel
} from '../../../../../services/bond-insurance-policy';
import { getCapitalizations } from '../../../../../services/capitalization';
import { getPolicies } from '../../../../../services/fire-insurance-policy';
import { translateStatusCapitalization } from '../../../../capitalization/util';
import { translateProposalStatus } from '../../../../fire-insurance/utils/Pottencial';
import { useHistory } from 'react-router-dom';

type RelatedPolicy = {
  id: string;
  createdAt: string;
  type: 'Fiança' | 'Incêndio' | 'Capitalização';
  insurerName: string;
  estateName: string;
  validityStartDate: string | null;
  validityEndDate: string | null;
  totalPremium: number;
  installments: number;
  installmentValue: number;
  status: string;
  validityStatus?: 'active' | 'expired' | 'pending';
  plan?: string;
};

const fetchRelatedPolicies = async (
  params: { document?: string; estateId: string },
  hiredDataId: string
): Promise<RelatedPolicy[]> => {
  const filters = {
    isRv: true,
    estateId: params.estateId,
    ...(params.document && { document: params.document })
  };

  const bondInsurances = await getBondInsurancePoliciesList(1, filters);
  const capitalizations = await getCapitalizations({ ...filters, page: '1' });
  const fireInsurances = await getPolicies(filters);

  const response: RelatedPolicy[] = [];
  try {
    bondInsurances.policies
      .filter(policy => policy.hiredDataTableId !== hiredDataId)
      .forEach(policy => {
        response.push({
          id: String(policy.bondInsuranceId),
          createdAt: String(policy.insurerPolicyCreatedAt),
          type: 'Fiança',
          insurerName: policy.insurer.name,
          estateName: policy.estate.name,
          validityStartDate: policy.rentInsuranceContractStartDate
            ? String(policy.rentInsuranceContractStartDate)
            : null,
          validityEndDate: policy.rentInsuranceContractEndDate
            ? String(policy.rentInsuranceContractEndDate)
            : null,
          totalPremium: policy.values.totalPremiumAmount,
          installments: policy.values.hiredInstallments,
          installmentValue: policy.values.hiredInstallmentsValue,
          status: getBondInsurancePolicyStatusLabel(policy.status)
        });
      });

    capitalizations.data.forEach(cap => {
      response.push({
        id: cap.id,
        createdAt: cap.createdAt,
        type: 'Capitalização',
        insurerName: cap.insurer?.name || '',
        estateName: cap.estate?.name || '',
        validityStartDate: null,
        validityEndDate: null,
        plan: cap.plan,
        totalPremium: Number(cap.totalValue),
        installments: 1,
        installmentValue: Number(cap.totalValue),
        status: translateStatusCapitalization(cap.status)
      });
    });

    fireInsurances.data.forEach((fire: any) => {
      console.log(fire);
      response.push({
        id: fire.fireInsuranceAnalysis.fireInsuranceId,
        createdAt: fire.createdAt,
        type: 'Incêndio',
        insurerName: fire.fireInsuranceAnalysis.insurer.name,
        estateName: fire.fireInsuranceAnalysis.fireInsurance.estate.name,
        validityStartDate: fire.policyPeriodStart ? String(fire.policyPeriodStart) : null,
        validityEndDate: fire.policyPeriodEnd ? String(fire.policyPeriodEnd) : null,
        totalPremium: Number(fire.fireInsuranceAnalysis.grossPremium),
        installments: fire.fireInsuranceAnalysis.installments,
        installmentValue: Number(fire.fireInsuranceAnalysis.value),
        status: translateProposalStatus(fire.status)
      });
    });
  } catch (error) {
    console.error('Erro ao mapear apólices relacionadas:', error);
    throw error;
  }

  return response;
};

export type RelatedPoliciesTableProps = {
  estateId: string;
  document?: string;
  hiredDataId: string;
};

const RelatedPoliciesTable: React.FC<RelatedPoliciesTableProps> = ({
  estateId,
  document,
  hiredDataId
}) => {
  const [relatedPolicies, setRelatedPolicies] = useState<RelatedPolicy[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();

  useEffect(() => {
    if (!estateId || !document) {
      setRelatedPolicies([]);
      setLoading(false);
      return;
    }
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchRelatedPolicies({ document, estateId }, hiredDataId);
        setRelatedPolicies(data);
      } catch (err) {
        console.error('Erro ao buscar apólices relacionadas:', err);
        setError('Não foi possível carregar as apólices relacionadas.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [estateId, document, hiredDataId]);

  const handleViewDetails = (redirectId: string, policyType: string) => {
    switch (policyType) {
      case 'Fiança':
        history.push(`./bond-insurance-policies`, { bondInsuranceId: redirectId });
        break;
      case 'Capitalização':
        history.push(`./capitalizations`, { capitalizationId: redirectId });
        break;
      case 'Incêndio':
        history.push(`./fire-insurance-policies/${redirectId}`);
        break;
    }
  };

  const renderValidityDates = (policy: RelatedPolicy) => {
    if (policy.type === 'Capitalização') {
      return policy.plan ? `${policy.plan} meses` : '-';
    }

    const startDateColor =
      policy.validityStatus === 'active'
        ? 'green'
        : policy.validityStatus === 'pending'
        ? 'orange'
        : 'black';
    const endDateColor = policy.validityStatus === 'expired' ? 'red' : 'black';
    const startDate = policy.validityStartDate
      ? new Date(policy.validityStartDate)
      : null;
    const endDate = policy.validityEndDate ? new Date(policy.validityEndDate) : null;

    const isStartDateValid = startDate && !isNaN(startDate.getTime());
    const isEndDateValid = endDate && !isNaN(endDate.getTime());

    return (
      <>
        {isStartDateValid && startDate ? (
          <span style={{ color: startDateColor }}>{DateToStringDate(startDate)}</span>
        ) : (
          '-'
        )}
        <br />
        {isEndDateValid && endDate ? (
          <span style={{ color: endDateColor }}>{DateToStringDate(endDate)}</span>
        ) : (
          '-'
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2em', minHeight: '150px' }}>
        <Dimmer active>
          <Loader inverted>Carregando...</Loader>
        </Dimmer>
      </div>
    );
  }

  if (error) {
    return <Message error content={error} style={{ margin: '1em 0' }} />;
  }

  if (relatedPolicies.length === 0) {
    return (
      <Message
        info
        content="Nenhuma apólice relacionada encontrada."
        style={{ margin: '1em 0' }}
      />
    );
  }

  return (
    <Table celled striped style={{ marginTop: '1em' }}>
      <Table.Header>
        <Table.Row style={{ backgroundColor: '#f9fafb' }}>
          <Table.HeaderCell>Criação</Table.HeaderCell>
          <Table.HeaderCell>Tipo</Table.HeaderCell>
          <Table.HeaderCell>Seguradora</Table.HeaderCell>
          <Table.HeaderCell>Imobiliária</Table.HeaderCell>
          <Table.HeaderCell>Vigência</Table.HeaderCell>
          <Table.HeaderCell>Prêmio total</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell>Ações</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {relatedPolicies.map(policy => (
          <Table.Row key={policy.id}>
            <Table.Cell>
              {policy.createdAt ? DateToStringDate(new Date(policy.createdAt)) : '-'}
            </Table.Cell>
            <Table.Cell>{policy.type}</Table.Cell>
            <Table.Cell>{policy.insurerName}</Table.Cell>
            <Table.Cell>{policy.estateName}</Table.Cell>
            <Table.Cell>{renderValidityDates(policy)}</Table.Cell>
            <Table.Cell>
              <span style={{ fontWeight: 'bold' }}>
                {maskMoney(String(policy.totalPremium), true, 2)}
              </span>
              <br />
              <span style={{ fontSize: '0.9em', color: '#555' }}>
                ({policy.installments}x){' '}
                {maskMoney(String(policy.installmentValue), true, 2)}
              </span>
            </Table.Cell>
            <Table.Cell>{policy.status}</Table.Cell>
            <Table.Cell textAlign="center">
              <Button
                icon
                size="small"
                style={{ backgroundColor: '#6d28d9', color: 'white' }}
                onClick={() => handleViewDetails(policy.id, policy.type)}
                title="Visualizar Apólice"
              >
                <Icon name="eye" />
              </Button>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

export default RelatedPoliciesTable;
